import { useState } from "react";
import { Loader2, Send, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "@/components/webadvisor/LeadDetails";

export const KINDS = [["audit", "Website audit report"], ["proposal", "Growth plan / proposal"], ["invoice", "Invoice"], ["statement", "Monthly statement"], ["upload", "Upload a PDF"]];

export const ShareReportForm = ({ leadId, options, onShared }) => {
  const [kind, setKind] = useState("audit");
  const [ref, setRef] = useState("");
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const items = options[kind] || [];

  const submit = async () => {
    setBusy(true);
    try {
      let r = ref;
      if (kind === "upload") {
        const fd = new FormData(); fd.append("file", file);
        r = (await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data.url;
      }
      const { data } = await api.post(`/api/webadvisor/leads/${leadId}/documents`, { kind, ref: r, title, message, notify, origin_url: window.location.origin });
      data.email_warning ? toast.warning(data.email_warning) : toast.success(notify ? `Sent — emailed to ${data.document.emailed_to}` : "Added to their portal");
      onShared(data.document); setRef(""); setFile(null); setTitle(""); setMessage("");
    } catch (err) { toast.error(errMsg(err, "Couldn't send the report")); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="share-report-form">
      <div><Label className="text-xs">What to send</Label>
        <Select value={kind} onValueChange={v => { setKind(v); setRef(""); }}>
          <SelectTrigger data-testid="share-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent>{KINDS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`share-kind-${k}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {kind === "upload" ? (
        <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg p-3 text-sm text-slate-600 cursor-pointer hover:border-blue-400" data-testid="share-upload-label">
          <Upload className="w-4 h-4" />{file ? file.name : "Choose a PDF (max 10MB)"}
          <input type="file" accept="application/pdf" className="hidden" onChange={e => setFile(e.target.files[0] || null)} data-testid="share-upload-input" />
        </label>
      ) : (
        <div><Label className="text-xs">Which one</Label>
          <Select value={ref} onValueChange={setRef}>
            <SelectTrigger data-testid="share-ref-select"><SelectValue placeholder={items.length ? "Pick one" : "Nothing available yet"} /></SelectTrigger>
            <SelectContent>{items.map(o => <SelectItem key={o.ref} value={o.ref}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div><Label className="text-xs">Title the customer sees (optional)</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Updated audit — October" data-testid="share-title-input" /></div>
      <div><Label className="text-xs">Message (optional)</Label><Textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} data-testid="share-message-input" /></div>
      <label className="flex items-center justify-between text-sm text-slate-700"><span>Also email the customer</span><Switch checked={notify} onCheckedChange={setNotify} data-testid="share-notify-switch" /></label>
      <button onClick={submit} disabled={busy || (kind === "upload" ? !file : !ref)} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-colors" data-testid="share-submit-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send to customer portal
      </button>
    </div>
  );
};
